import React, { useState, useCallback, useMemo } from "react";
import Dialog from "./Dialog";
import { DialogContext } from "./DialogContext";
import PropTypes from "prop-types";



const DialogProvider = ({ children }) => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    config: {},
  });

  const showDialog = useCallback((config) => {
    setDialogState({
      isOpen: true,
      config,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState({
      isOpen: false,
      config: {},
    });
  }, []);

  const showSuccess = useCallback(
    (message, options = {}) => {
      showDialog({
        type: "success",
        message,
        ...options,
      });
    },
    [showDialog]
  );

  const showError = useCallback(
    (message, options = {}) => {
      showDialog({
        type: "error",
        message,
        ...options,
      });
    },
    [showDialog]
  );

  const showWarning = useCallback(
    (message, options = {}) => {
      showDialog({
        type: "warning",
        message,
        ...options,
      });
    },
    [showDialog]
  );

  const showInfo = useCallback(
    (message, options = {}) => {
      showDialog({
        type: "info",
        message,
        ...options,
      });
    },
    [showDialog]
  );

  const showConfirm = useCallback(
    (message, options = {}) => {
      return new Promise((resolve) => {
        showDialog({
          type: "confirm",
          message,
          showCancel: true,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
          ...options,
        });
      });
    },
    [showDialog]
  );

  const value = useMemo(
    () => ({
      showDialog,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showConfirm,
      closeDialog,
    }),
    [
      showDialog,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showConfirm,
      closeDialog,
    ]
  );

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Dialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        config={dialogState.config}
      />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </DialogContext.Provider>
  );
};
DialogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
export default DialogProvider;